import { Theme } from '@ux-aspects-universal/core/theming';
export { Theme } from '@ux-aspects-universal/core/theming';
import * as i0 from '@angular/core';
import { InjectionToken, inject, CSP_NONCE, Injectable, computed, ElementRef, Directive, Input, DestroyRef, ViewContainerRef, TemplateRef, NgModule, APP_INITIALIZER } from '@angular/core';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DOCUMENT } from '@angular/common';
import { map, distinctUntilChanged } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';

/**
 * Helper function to create an object that represents a feature.
 */
function createFeature(kind, providers) {
  return {
    ɵkind: kind,
    ɵproviders: providers
  };
}
var FontFamily;
(function (FontFamily) {
  FontFamily["Roboto"] = "Roboto";
  FontFamily["Inter"] = "Inter";
})(FontFamily || (FontFamily = {}));
const UxaFontFamilyToken = new InjectionToken('Font Fmaily Theme Token');
function injectFontFamily() {
  return inject(UxaFontFamilyToken, {
    optional: true
  }) ?? FontFamily.Roboto;
}
/**
 * Change the default font family for the application.
 */
function withExperimentalInterFontFamily() {
  return createFeature(0 /* ThemeFeatureKind.InterFontFeature */, [{
    provide: UxaFontFamilyToken,
    useValue: FontFamily.Inter
  }]);
}

/**
 * A map that emits a new value whenever it is updated
 */
class ObservableMap extends Map {
  constructor() {
    super(...arguments);
    /** Store the subject that will emit the map */
    this._subject = new BehaviorSubject(this);
    /** Store the subject as a readonly observable */
    this._observable = this._subject.asObservable();
  }
  /**
   * Set a value in the map and emit the new map
   * @param key The key to use
   * @param value The value to set
   */
  set(key, value) {
    super.set(key, value);
    this._subject.next(this);
    return this;
  }
  /**
   * Delete a value from the map and emit the new map
   * @param key The key to delete
   */
  delete(key) {
    const result = super.delete(key);
    this._subject.next(this);
    return result;
  }
  /**
   * Clear the map and emit the new map
   */
  clear() {
    super.clear();
    this._subject.next(this);
  }
  /**
   * Subscribe to the dictionary as an observable.
   * @param observer The observer.
   * @returns The subscription.
   */
  subscribe(observer) {
    return this._subject.subscribe(observer);
  }
  /**
   * Access the underlying subject as an observable
   */
  observable() {
    return this._observable;
  }
  /**
   * Manually notify observers of a change even if the map has not been updated
   */
  notifyObservers() {
    this._subject.next(this);
  }
  /**
   * Remove all subscriptions and complete the subject
   */
  destroy() {
    this._subject.complete();
  }
}

/**
 * An immutable construct that allow a color to be represented
 * as an object
 */
class UxaColor {
  constructor(_red, _green, _blue, _alpha = 1) {
    this._red = _red;
    this._green = _green;
    this._blue = _blue;
    this._alpha = _alpha;
  }
  /**
   * Get the value of the red color component
   */
  getRed() {
    return this._red;
  }
  /**
   * Get the value of the green color component
   */
  getGreen() {
    return this._green;
  }
  /**
   * Get the value of the blue color component
   */
  getBlue() {
    return this._blue;
  }
  /**
   * Get the value of the alpha color component
   */
  getAlpha() {
    return this._alpha;
  }
  /**
   * Create a new immutable color object with an updated red value
   * @param red The red color component value
   */
  setRed(red) {
    return UxaColor.fromRgba(red, this._green, this._blue, this._alpha);
  }
  /**
   * Create a new immutable color object with an updated green value
   * @param green The green color component value
   */
  setGreen(green) {
    return UxaColor.fromRgba(this._red, green, this._blue, this._alpha);
  }
  /**
   * Create a new immutable color object with an updated blue value
   * @param blue The blue color component value
   */
  setBlue(blue) {
    return UxaColor.fromRgba(this._red, this._green, blue, this._alpha);
  }
  /**
   * Get the color in the form of an RGB color string
   */
  toRgb() {
    return `rgb(${this._red}, ${this._green}, ${this._blue})`;
  }
  /**
   * Get the color in the form of an RGBA color string
   */
  toRgba() {
    return `rgba(${this._red}, ${this._green}, ${this._blue}, ${this._alpha})`;
  }
  /**
   * Get the color in the form of an Hex color string
   */
  toHex() {
    const red = this._red.toString(16).padStart(2, '0');
    const green = this._green.toString(16).padStart(2, '0');
    const blue = this._blue.toString(16).padStart(2, '0');
    return `#${red}${green}${blue}`;
  }
  /**
   * Create a new immutable color object with an updated alpha value
   * @param alpha The alpha color component value
   */
  setAlpha(alpha) {
    return UxaColor.fromRgba(this._red, this._green, this._blue, alpha);
  }
  /**
   * Determine if the color value is equal to another UxaColor
   * @param color The color to compare against
   */
  equals(color) {
    return this.getRed() === color.getRed() && this.getGreen() === color.getGreen() && this.getBlue() === color.getBlue() && this.getAlpha() === color.getAlpha();
  }
  /**
   * Instantiate a new UxaColor object from an RGB value
   * @param red The red value
   * @param green The green value
   * @param blue The blue value
   */
  static fromRgb(red, green, blue) {
    return new UxaColor(red, green, blue);
  }
  /**
   * Instantiate a new UxaColor object from an RGBA value
   * @param red The red value
   * @param green The green value
   * @param blue The blue value
   * @param alpha The alpha value
   */
  static fromRgba(red, green, blue, alpha) {
    return new UxaColor(red, green, blue, alpha);
  }
  /**
   * Instantiate a new UxaColor object from a color value
   * @param value The RGB, RGBA or hex code
   */
  static fromString(value) {
    // remove any surrounding whitespace
    value = value.trim();
    if (this.isRgba(value)) {
      const [_, red, green, blue, alpha] = value.match(rgbaPattern);
      return UxaColor.fromRgba(parseInt(red), parseInt(green), parseInt(blue), parseFloat(alpha ?? 1));
    }
    if (this.isLongHex(value)) {
      const [_, red, green, blue] = value.match(longHexPattern);
      return UxaColor.fromRgb(parseInt(red, 16), parseInt(green, 16), parseInt(blue, 16));
    }
    if (this.isShortHex(value)) {
      const [_, red, green, blue] = value.match(shortHexPattern);
      return UxaColor.fromRgb(parseInt(red + red, 16), parseInt(green + green, 16), parseInt(blue + blue, 16));
    }
    throw new Error(`UxaColor: ${value} is not a valid hex code`);
  }
  /**
   * Create a color from an existing UxaColor
   * @param color The color object to clone
   */
  static fromColor(color) {
    return UxaColor.fromRgba(color.getRed(), color.getGreen(), color.getBlue(), color.getAlpha());
  }
  /**
   * Determine if a value is a long hex code
   * @param value The potential long hex code
   */
  static isLongHex(value) {
    return Boolean(value.match(longHexPattern));
  }
  /**
   * Determine if a value is a short hex code
   * @param value The potential short hex code
   */
  static isShortHex(value) {
    return Boolean(value.match(shortHexPattern));
  }
  /**
   * Determine if a value is a RGB(A) value
   * @param value The potential RGB(A) value
   */
  static isRgba(value) {
    return Boolean(value.match(rgbaPattern));
  }
}
const rgbaPattern = /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/;
const shortHexPattern = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
const longHexPattern = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;
class UxaThemeManager {
  constructor() {
    /** Access the document */
    this._document = inject(DOCUMENT);
    /** Access the CSP nonce value */
    this._cspNonce = inject(CSP_NONCE, {
      optional: true
    });
    /**
     * Themes may be applied at the root level or at the element level.
     * This map stores the theme name against the element that it is applied to.
     */
    this._elementThemes = new ObservableMap();
    /** Access the theme applied to the root element */
    this.theme$ = this._elementThemes.observable().pipe(map(elementMap => elementMap.get(this._document.body)), distinctUntilChanged());
    /** Store the stylesheets loaders */
    this._stylesheets = new Map();
    /** Store all the loaded stylesheets */
    this._loadedStylesheets = new Map();
    /** The active font family */
    this._fontFamily = FontFamily.Roboto;
    // create the style element that will store the CSS variables
    this._styleElement = this._document.createElement('style');
    // add the CSP nonce if it exists
    if (this._cspNonce) {
      this._styleElement.setAttribute('nonce', this._cspNonce);
    }
    // add the style tag to the document head
    this._document.head.appendChild(this._styleElement);
  }
  /** @internal */
  ngOnDestroy() {
    this._document.head.removeChild(this._styleElement);
    this._elementThemes.destroy();
  }
  /**
   * Set the active theme
   * @param theme The theme to activate
   * @param target The element to apply the theme to
   */
  async setTheme(theme, target = this._document.body) {
    // remove the old theme class on the body element
    if (this.getTheme(target)) {
      const themeClasses = this.getThemeClasses(this.getTheme(target));
      for (const themeClass of themeClasses) {
        target.classList.remove(themeClass);
      }
    }
    // if there is a stylesheet to load for this theme then do so before updating the theme observable
    await this.loadStylesheet(theme);
    // add the new theme class
    const themeClasses = this.getThemeClasses(theme);
    for (const themeClass of themeClasses) {
      target.classList.add(themeClass);
    }
    // store the theme against the element
    this._elementThemes.set(target, theme);
  }
  /**
   * Return the name of the current theme
   * @param target The element to get the theme from
   */
  getTheme(target = this._document.body) {
    return this._elementThemes.get(target);
  }
  /**
   * Remove theme from the element
   */
  removeTheme(target = this._document.body) {
    // remove the theme class from the element
    if (this.getTheme(target)) {
      const themeClasses = this.getThemeClasses(this.getTheme());
      for (const themeClass of themeClasses) {
        target.classList.remove(themeClass);
      }
    }
    // remove the theme from the map
    this._elementThemes.delete(target);
  }
  /**
   * Load a stylesheet using the provided loader
   * @param theme The theme to load
   */
  async loadStylesheet(theme) {
    // if the stylesheet has already been loaded then we can skip this step
    if (this._loadedStylesheets.has(theme)) {
      return;
    }
    const themeLoader = this._stylesheets.get(theme);
    // load the stylesheet and store it in the cache
    if (themeLoader) {
      this._loadedStylesheets.set(theme, await themeLoader());
      // update the styles tag to contain all the loaded stylesheets
      this._styleElement.innerHTML = Array.from(this._loadedStylesheets.values()).join('');
    }
  }
  /**
   * Provide the theme service with a stylesheet it should load if that theme is activated
   * @param theme The theme the loader is for
   * @param loader The function that will load the theme
   */
  withStylesheet(theme, loader) {
    this._stylesheets.set(theme, loader);
    return this;
  }
  /**
   * Access the value of a specific property
   * @param property The name of the property
   * @param target The element to access the property from
   */
  getProperty(property, target = this._document.documentElement) {
    return getComputedStyle(target).getPropertyValue(property);
  }
  /**
   * Get the current property value and listen for future changes
   * @param property The name of the property
   * @param target The element to watch for changes
   */
  watchProperty(property, target = this._document.body) {
    return this._elementThemes.observable().pipe(map(() => this.getProperty(property, target)), distinctUntilChanged());
  }
  /**
   * Update the value of a specific CSS property
   * @param property The name of the property
   * @param value The new property value
   */
  setProperty(property, value, element = this._document.documentElement) {
    getComputedStyle(element).setProperty(property, value);
    // while the theme hasn't changed as such, a property on it has so we can
    // use this observable to inform any watchers of a potential update.
    // the public `theme$` observable has a `distinctUntilChanged` check so
    // it will not emit unexpectedly to any subscribers.
    this._elementThemes.notifyObservers();
  }
  /**
   * Get a color from the theme
   * @param color The name of the color
   * @param target The element to get the color from
   */
  getColor(color, target = this._document.body) {
    const value = this.getProperty(`--uxa-color__${color}`, target);
    // if the color does not exist then we should return a default color
    // this will prevent any unexpected runtime errors if the application
    // does not have sufficient null checks
    if (!value) {
      throw new Error(`UxaColor: Could not find a value for "${color}" in the active theme`);
    }
    return UxaColor.fromString(value);
  }
  /**
   * Determine if a color exists in the theme
   * @param color The name of the color
   * @param target The element to check the color on
   */
  hasColor(color, target = this._document.body) {
    return !!this.getProperty(`--uxa-color__${color}`, target);
  }
  /**
   * Get a color from the theme and listen for future changes
   * @param color The name of the color
   * @param target The element to watch for changes
   */
  watchColor(color, target = this._document.body) {
    return this._elementThemes.observable().pipe(map(() => this.getColor(color, target)), distinctUntilChanged((oldColor, newColor) => newColor.equals(oldColor)));
  }
  /**
   * Ensure the theme name is converted into a lowercase kebab cased string
   * @param theme The theme name
   * @internal
   */
  getThemeClasses(theme) {
    theme = theme.replace(/([a-z\d])([A-Z])/g, '$1_$2').toLowerCase().replace(/[ _]/g, '-');
    const classes = [`uxa-theme-${theme}`];
    if (this._fontFamily === FontFamily.Inter) {
      classes.push('uxa-feature-inter-font');
    }
    return classes;
  }
  /**
   * Get the current theme name and listen for future changes
   * @param target The element to watch for changes
   */
  watchTheme(target = this._document.body) {
    return this._elementThemes.observable().pipe(map(() => this.getTheme(target)), distinctUntilChanged());
  }
  /**
   * @internal Specify the font family to use for the theme
   */
  setFontFamily(fontFamily) {
    this._fontFamily = fontFamily;
  }
  static {
    this.ɵfac = function UxaThemeManager_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaThemeManager)();
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: UxaThemeManager,
      factory: UxaThemeManager.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaThemeManager, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [], null);
})();
/**
 * Get the current active theme
 */
function injectTheme() {
  const themeManager = inject(UxaThemeManager);
  return toSignal(themeManager.theme$);
}
/**
 * Determine whether the current theme is a Jato theme
 */
function injectIsJatoTheme() {
  const theme = injectTheme();
  return computed(() => theme() === Theme.JatoLight || theme() === Theme.JatoDark);
}
const ThemeToken = new InjectionToken('Theme Token');
const UxaThemeElementToken = new InjectionToken('UxaThemeElement');
class UxaThemeDirective {
  constructor() {
    /** Get the element to apply the theme to */
    this._elementRef = inject(ElementRef);
    /** Get the theme manager service */
    this._themeManager = inject(UxaThemeManager);
  }
  /** Set the theme on the element */
  set theme(theme) {
    // if no theme is provided, do nothing. We need this for cases such as the dialog where the theme
    // is provided optionally as part of the config, so it may not be defined, and only if it is do we
    // want to apply it.
    if (!theme) {
      this._themeManager.removeTheme(this._elementRef.nativeElement);
      return;
    }
    this._themeManager.setTheme(theme, this._elementRef.nativeElement);
  }
  static {
    this.ɵfac = function UxaThemeDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaThemeDirective)();
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: UxaThemeDirective,
      selectors: [["", "uxaTheme", ""]],
      inputs: {
        theme: [0, "uxaTheme", "theme"]
      },
      features: [i0.ɵɵProvidersFeature([{
        provide: UxaThemeElementToken,
        useExisting: ElementRef
      }])]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaThemeDirective, [{
    type: Directive,
    args: [{
      selector: '[uxaTheme]',
      providers: [{
        provide: UxaThemeElementToken,
        useExisting: ElementRef
      }],
      standalone: true
    }]
  }], null, {
    theme: [{
      type: Input,
      args: ['uxaTheme']
    }]
  });
})();
class UxaShowOnThemeDirective {
  constructor() {
    this._destroyRef = inject(DestroyRef);
    this._theme = inject(ThemeToken);
    this._viewContainerRef = inject(ViewContainerRef);
    this._templateRef = inject(TemplateRef);
    this._themeManager = inject(UxaThemeManager);
    this._themeElement = inject(UxaThemeElementToken, {
      optional: true
    });
  }
  ngOnInit() {
    this._themeManager.watchTheme(this._themeElement?.nativeElement).pipe(takeUntilDestroyed(this._destroyRef)).subscribe(theme => {
      // if no theme is provided use the injected theme token
      if (!theme) {
        theme = this._theme;
      }
      this._viewContainerRef.clear();
      const isVisible = theme && this._visibleOnThemes.includes(theme);
      if (isVisible) {
        const viewRef = this._viewContainerRef.createEmbeddedView(this._templateRef);
        viewRef.detectChanges();
      }
    });
  }
  static {
    this.ɵfac = function UxaShowOnThemeDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaShowOnThemeDirective)();
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: UxaShowOnThemeDirective,
      standalone: false
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaShowOnThemeDirective, [{
    type: Directive
  }], null, null);
})();
/**
 * @internal
 * This directive is used to show/hide specific elements when any Micro Focus theme is active
 */
class UxaShowOnMicroFocusDirective extends UxaShowOnThemeDirective {
  constructor() {
    super(...arguments);
    this._visibleOnThemes = [Theme.MicroFocus, Theme.MicroFocusDark, Theme.WhiteLabel];
  }
  static {
    this.ɵfac = /* @__PURE__ */(() => {
      let ɵUxaShowOnMicroFocusDirective_BaseFactory;
      return function UxaShowOnMicroFocusDirective_Factory(__ngFactoryType__) {
        return (ɵUxaShowOnMicroFocusDirective_BaseFactory || (ɵUxaShowOnMicroFocusDirective_BaseFactory = i0.ɵɵgetInheritedFactory(UxaShowOnMicroFocusDirective)))(__ngFactoryType__ || UxaShowOnMicroFocusDirective);
      };
    })();
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: UxaShowOnMicroFocusDirective,
      selectors: [["", "uxaShowOnMicroFocus", ""]],
      features: [i0.ɵɵInheritDefinitionFeature]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaShowOnMicroFocusDirective, [{
    type: Directive,
    args: [{
      selector: '[uxaShowOnMicroFocus]',
      standalone: true
    }]
  }], null, null);
})();
/**
 * @internal
 * This directive is used to show/hide specific elements when any OpenText theme is active
 */
class UxaShowOnOpenTextDirective extends UxaShowOnThemeDirective {
  constructor() {
    super(...arguments);
    this._visibleOnThemes = [Theme.OpenTextStage1, Theme.OpenTextStage1Dark];
  }
  static {
    this.ɵfac = /* @__PURE__ */(() => {
      let ɵUxaShowOnOpenTextDirective_BaseFactory;
      return function UxaShowOnOpenTextDirective_Factory(__ngFactoryType__) {
        return (ɵUxaShowOnOpenTextDirective_BaseFactory || (ɵUxaShowOnOpenTextDirective_BaseFactory = i0.ɵɵgetInheritedFactory(UxaShowOnOpenTextDirective)))(__ngFactoryType__ || UxaShowOnOpenTextDirective);
      };
    })();
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: UxaShowOnOpenTextDirective,
      selectors: [["", "uxaShowOnOpenText", ""]],
      features: [i0.ɵɵInheritDefinitionFeature]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaShowOnOpenTextDirective, [{
    type: Directive,
    args: [{
      selector: '[uxaShowOnOpenText]',
      standalone: true
    }]
  }], null, null);
})();
/**
 * @internal
 * This directive is used to show/hide specific elements when any OpenText theme is active
 */
class UxaShowOnJatoDirective extends UxaShowOnThemeDirective {
  constructor() {
    super(...arguments);
    this._visibleOnThemes = [Theme.JatoLight, Theme.JatoDark];
  }
  static {
    this.ɵfac = /* @__PURE__ */(() => {
      let ɵUxaShowOnJatoDirective_BaseFactory;
      return function UxaShowOnJatoDirective_Factory(__ngFactoryType__) {
        return (ɵUxaShowOnJatoDirective_BaseFactory || (ɵUxaShowOnJatoDirective_BaseFactory = i0.ɵɵgetInheritedFactory(UxaShowOnJatoDirective)))(__ngFactoryType__ || UxaShowOnJatoDirective);
      };
    })();
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: UxaShowOnJatoDirective,
      selectors: [["", "uxaShowOnJato", ""]],
      features: [i0.ɵɵInheritDefinitionFeature]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaShowOnJatoDirective, [{
    type: Directive,
    args: [{
      selector: '[uxaShowOnJato]',
      standalone: true
    }]
  }], null, null);
})();

/**
 * @internal
 * This directive is used to apply the theme class to the current element. This is
 * useful when forwarding the current theme to a child component that does is not
 * a child in the DOM tree, e.g. a child component that is rendered in an overlay.
 */
class UxaThemeClassDirective {
  constructor() {
    this._destroyRef = inject(DestroyRef);
    /** Find the parent theme element */
    this._themeElement = inject(UxaThemeElementToken, {
      optional: true
    });
    /** Get the theme manager service */
    this._themeManager = inject(UxaThemeManager);
    /** Get the element ref */
    this._elementRef = inject(ElementRef);
    /** Store the current theme class */
    this._themeClasses = [];
  }
  ngOnInit() {
    // apply the theme class to the parent theme element
    this._themeManager.watchTheme(this._themeElement?.nativeElement).pipe(takeUntilDestroyed(this._destroyRef)).subscribe(theme => {
      // remove the current theme class
      for (const themeClass of this._themeClasses) {
        this._elementRef.nativeElement.classList.remove(themeClass);
      }
      this._themeClasses = theme ? this._themeManager.getThemeClasses(theme) : [];
      // add the new theme class
      for (const themeClass of this._themeClasses) {
        this._elementRef.nativeElement.classList.add(themeClass);
      }
    });
  }
  static {
    this.ɵfac = function UxaThemeClassDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaThemeClassDirective)();
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: UxaThemeClassDirective,
      selectors: [["", "uxaThemeClass", ""]]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaThemeClassDirective, [{
    type: Directive,
    args: [{
      selector: '[uxaThemeClass]',
      standalone: true
    }]
  }], null, null);
})();
const ThemesToken = new InjectionToken('Themes Token');

/**
 * App Initializers get called before the application will appear.
 * This allows us to perform async requests and wait for the response
 * before the app will initially show.
 * We can therefore load any appropriate theme stylesheet without the
 * UI flickering from an unstyled state to styled state once the stylesheet
 * loads.
 *
 */
function initTheme() {
  const themeManager = inject(UxaThemeManager);
  const theme = inject(ThemeToken);
  const themes = inject(ThemesToken);
  const fontFamily = injectFontFamily();
  // provide the font family to the theme manager
  themeManager.setFontFamily(fontFamily);
  // We can't return this directly as Angular's static
  // type checking will fail the build. This is a simple workaround.
  const loader = async () => {
    // register each theme stylesheet
    for (const themeName in themes) {
      themeManager.withStylesheet(themeName, themes[themeName]);
    }
    // set the initial theme
    await themeManager.setTheme(theme);
  };
  return loader;
}
/**
 * This module is only a utility for setting the initial theme
 * and defining the stylesheets for each theme
 */
class UxaThemeModule {
  static forRoot(theme, themes, features = []) {
    return {
      ngModule: UxaThemeModule,
      providers: provideThemes({
        theme,
        themes
      }, ...features)
    };
  }
  static {
    this.ɵfac = function UxaThemeModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaThemeModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: UxaThemeModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaThemeModule, [{
    type: NgModule,
    args: [{
      imports: [UxaThemeDirective, UxaThemeClassDirective, UxaShowOnMicroFocusDirective, UxaShowOnOpenTextDirective],
      exports: [UxaThemeDirective, UxaThemeClassDirective, UxaShowOnMicroFocusDirective, UxaShowOnOpenTextDirective]
    }]
  }], null, null);
})();
/**
 * Configure the initial theme and the stylesheets to
 * load for each theme.
 * @param config The theme configuration
 */
function provideThemes(config, ...features) {
  return [{
    provide: ThemesToken,
    useValue: config.themes ?? {}
  }, {
    provide: ThemeToken,
    useValue: config.theme
  }, ...features.map(f => f.ɵproviders), {
    provide: APP_INITIALIZER,
    useFactory: initTheme,
    multi: true
  }];
}

/**
 * Generated bundle index. Do not edit.
 */

export { UxaColor, UxaShowOnJatoDirective, UxaShowOnMicroFocusDirective, UxaShowOnOpenTextDirective, UxaThemeClassDirective, UxaThemeDirective, UxaThemeElementToken, UxaThemeManager, UxaThemeModule, initTheme, injectIsJatoTheme, injectTheme, provideThemes, withExperimentalInterFontFamily };
